import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Form, Modal, Spinner } from 'react-bootstrap';
import ccmService from '../../services/ccmService';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import './Billing.css';
import AppDateInput from '../../components/Common/AppDateInput/AppDateInput';
import {
  buildPatientDetailsPath,
  resolvePatientRecordId,
  resolvePatientPracticeId
} from '../../utils/patientRouteUtils';

const Billing = () => {
  const navigate = useNavigate();
  const { clearBreadcrumbs } = useBreadcrumb();
  const [loading, setLoading] = useState(true);
  const [billingCycles, setBillingCycles] = useState([]);
  const [qualifyingPatients, setQualifyingPatients] = useState([]);
  const [claims, setClaims] = useState([]);
  const [practiceId, setPracticeId] = useState('');
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [showQualifyingModal, setShowQualifyingModal] = useState(false);
  const [newCycle, setNewCycle] = useState({
    practice_id: '',
    billing_month: '',
    period_start_date: '',
    period_end_date: ''
  });
  const [billingMonth, setBillingMonth] = useState('');

  useEffect(() => {
    clearBreadcrumbs();
  }, [clearBreadcrumbs]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pid = urlParams.get('practiceId') || '';
    setPracticeId(pid);
    
    if (pid) {
      fetchBillingCycles(pid);
      setNewCycle(prev => ({ ...prev, practice_id: pid }));
    } else {
      // If no practiceId, still show UI (empty state)
      setLoading(false);
    }
  }, []);

  const fetchBillingCycles = async (pid) => {
    try {
      setLoading(true);
      const response = await ccmService.getBillingCycles({ practiceId: pid });
      if (response.success) {
        setBillingCycles(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching billing cycles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQualifyingPatients = async (pid, month) => {
    try {
      setLoading(true);
      const response = await ccmService.getQualifyingPatients(pid, month);
      if (response.success) {
        setQualifyingPatients(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching qualifying patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClaims = async (cycleId) => {
    try {
      const response = await ccmService.getClaims({ billing_cycle_id: cycleId });
      if (response.success) {
        setClaims(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
    }
  };

  const handleCreateCycle = async () => {
    try {
      const response = await ccmService.createBillingCycle(newCycle);
      if (response.success) {
        setShowCycleModal(false);
        setNewCycle({
          practice_id: practiceId,
          billing_month: '',
          period_start_date: '',
          period_end_date: ''
        });
        fetchBillingCycles(practiceId);
      }
    } catch (error) {
      console.error('Error creating billing cycle:', error);
      alert('Failed to create billing cycle');
    }
  };

  const handleGenerateEncounters = async () => {
    if (!selectedCycle || qualifyingPatients.length === 0) {
      alert('Please select a billing cycle and ensure there are qualifying patients');
      return;
    }
    
    try {
      const enrollmentIds = qualifyingPatients.map(p => p.enrollment_id);
      const response = await ccmService.generateEncounters({
        practice_id: practiceId,
        billing_cycle_id: selectedCycle,
        patient_ids: enrollmentIds,
        service_date: new Date().toISOString().split('T')[0]
      });
      
      if (response.success) {
        alert('Encounters generated successfully');
      }
    } catch (error) {
      console.error('Error generating encounters:', error);
      alert('Failed to generate encounters');
    }
  };

  const handleCreateClaims = async () => {
    if (!selectedCycle || qualifyingPatients.length === 0) {
      alert('Please select a billing cycle and ensure there are qualifying patients');
      return;
    }
    
    if (!window.confirm(`Create claims for ${qualifyingPatients.length} patients?`)) {
      return;
    }
    
    try {
      setLoading(true);
      const enrollmentIds = qualifyingPatients.map(p => p.enrollment_id);
      const response = await ccmService.createClaims({
        billing_cycle_id: selectedCycle,
        enrollment_ids: enrollmentIds,
        service_date: new Date().toISOString().split('T')[0]
      });
      
      if (response.success) {
        alert(`Claims created successfully: ${response.data.claims_created}`);
        if (response.data.blocked_count > 0) {
          alert(`${response.data.blocked_count} patient(s) were blocked due to billing conflicts.`);
        }
        fetchClaims(selectedCycle);
        setShowQualifyingModal(false);
      }
    } catch (error) {
      console.error('Error creating claims:', error);
      alert('Failed to create claims');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  if (loading && billingCycles.length === 0) {
    return (
      <div className="ccm-bill-container">
        <div className="ccm-bill-loading">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  return (
    <div className="ccm-bill-container">
      <div className="ccm-bill-page-title">
        <h3 className="ccm-bill-title-text">CCM Billing</h3>
        <button className="ccm-bill-btn" onClick={() => setShowCycleModal(true)}>
          Create Billing Cycle
        </button>
      </div>

      {/* Billing Cycles */}
      <Row className="mb-4">
        <Col>
          <div className="ccm-bill-card">
            <div className="ccm-bill-card-header">
              <h5 className="ccm-bill-card-title">Billing Cycles</h5>
            </div>
            <div className="ccm-bill-card-body">
              <div className="ccm-bill-table-container">
                <div className="ccm-bill-table-responsive">
                  <table className="ccm-bill-table">
                <thead>
                  <tr>
                    <th>Billing Month</th>
                    <th>Period Start</th>
                    <th>Period End</th>
                    <th>Status</th>
                    <th>Total Patients</th>
                    <th>Total Claims</th>
                    <th>Total Amount</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {billingCycles.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="ccm-bill-empty-cell">No billing cycles found</td>
                    </tr>
                  ) : (
                    billingCycles.map((cycle) => (
                      <tr key={cycle.id}>
                        <td>{cycle.billing_month}</td>
                        <td>{cycle.period_start_date ? new Date(cycle.period_start_date).toLocaleDateString() : 'N/A'}</td>
                        <td>{cycle.period_end_date ? new Date(cycle.period_end_date).toLocaleDateString() : 'N/A'}</td>
                        <td>
                          <span className={`ccm-bill-badge ${
                            cycle.status === 'open' ? 'ccm-bill-badge-open' :
                            cycle.status === 'closed' ? 'ccm-bill-badge-closed' : 'ccm-bill-badge-open'
                          }`}>
                            {cycle.status}
                          </span>
                        </td>
                        <td>{cycle.total_patients || 0}</td>
                        <td>{cycle.total_claims || 0}</td>
                        <td>${(cycle.total_amount || 0).toFixed(2)}</td>
                        <td>
                          <div className="ccm-bill-action-buttons">
                            <button
                              className="ccm-bill-action-btn ccm-bill-btn-view"
                              onClick={() => {
                                setSelectedCycle(cycle.id);
                                setBillingMonth(cycle.billing_month);
                                fetchQualifyingPatients(practiceId, cycle.billing_month);
                                setShowQualifyingModal(true);
                              }}
                            >
                              View Patients
                            </button>
                            <button
                              className="ccm-bill-action-btn ccm-bill-btn-view"
                              onClick={() => {
                                setSelectedCycle(cycle.id);
                                fetchClaims(cycle.id);
                              }}
                            >
                              View Claims
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
        </Col>
      </Row>

      {/* Claims Table */}
      {claims.length > 0 && (
        <Row>
          <Col>
            <div className="ccm-bill-card">
              <div className="ccm-bill-card-header">
                <h5 className="ccm-bill-card-title">Claims</h5>
              </div>
              <div className="ccm-bill-card-body">
                <div className="ccm-bill-table-container">
                  <div className="ccm-bill-table-responsive">
                    <table className="ccm-bill-table">
                  <thead>
                    <tr>
                      <th>Claim Number</th>
                      <th>Patient</th>
                      <th>Service Date</th>
                      <th>CPT Code</th>
                      <th>Total Time</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claims.map((claim) => (
                      <tr
                        key={claim.id}
                        style={{ cursor: claim.patient_id ? 'pointer' : 'default' }}
                        onClick={() => {
                          if (claim.patient_id) {
                            const path = buildPatientDetailsPath(practiceId || claim.practice_id, claim.patient_id);
                            if (path) navigate(path);
                          }
                        }}
                      >
                        <td>{claim.claim_number}</td>
                        <td>
                          {claim.patient_id ? (
                            <a
                              href="#"
                              style={{ color: '#007bff', textDecoration: 'none', fontWeight: 600 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const path = buildPatientDetailsPath(practiceId || claim.practice_id, claim.patient_id);
                                if (path) navigate(path);
                              }}
                            >
                              {claim.patient_name || 'N/A'}
                            </a>
                          ) : (
                            claim.patient_name || 'N/A'
                          )}
                        </td>
                        <td>{claim.service_date ? new Date(claim.service_date).toLocaleDateString() : 'N/A'}</td>
                        <td>{claim.cpt_code}</td>
                        <td>{formatTime(claim.total_time)}</td>
                        <td>${(claim.amount || 0).toFixed(2)}</td>
                        <td>
                          <span className={`ccm-bill-badge ${
                            claim.claim_status === 'paid' ? 'ccm-bill-badge-paid' :
                            claim.claim_status === 'submitted' ? 'ccm-bill-badge-submitted' :
                            claim.claim_status === 'rejected' ? 'ccm-bill-badge-rejected' : 'ccm-bill-badge-open'
                          }`}>
                            {claim.claim_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
          </Col>
        </Row>
      )}

      {/* Create Cycle Modal */}
      <Modal show={showCycleModal} onHide={() => setShowCycleModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Create Billing Cycle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Billing Month (YYYY-MM) *</Form.Label>
              <Form.Control
                type="month"
                value={newCycle.billing_month}
                onChange={(e) => {
                  const month = e.target.value;
                  setNewCycle({
                    ...newCycle,
                    billing_month: month,
                    period_start_date: month + '-01',
                    period_end_date: month + '-' + new Date(month + '-01').toISOString().substring(8, 10)
                  });
                }}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Period Start Date *</Form.Label>
              <AppDateInput
                className="form-control"
                value={newCycle.period_start_date}
                onChange={(v) => setNewCycle({ ...newCycle, period_start_date: v })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Period End Date *</Form.Label>
              <AppDateInput
                className="form-control"
                value={newCycle.period_end_date}
                onChange={(v) => setNewCycle({ ...newCycle, period_end_date: v })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <div className="ccm-bill-modal-footer">
          <button className="ccm-bill-btn-secondary" onClick={() => setShowCycleModal(false)}>
            Cancel
          </button>
          <button className="ccm-bill-btn ccm-bill-btn-primary" onClick={handleCreateCycle}>
            Create Cycle
          </button>
        </div>
      </Modal>

      {/* Qualifying Patients Modal */}
      <Modal show={showQualifyingModal} onHide={() => setShowQualifyingModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Qualifying Patients - {billingMonth}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ccm-bill-button-group">
            <button
              className="ccm-bill-btn ccm-bill-btn-success"
              onClick={handleGenerateEncounters}
              disabled={qualifyingPatients.length === 0}
            >
              Generate Encounters
            </button>
            <button
              className="ccm-bill-btn ccm-bill-btn-primary"
              onClick={handleCreateClaims}
              disabled={qualifyingPatients.length === 0 || loading}
            >
              {loading ? 'Creating...' : 'Create Claims'}
            </button>
          </div>
          <div className="ccm-bill-table-container">
            <div className="ccm-bill-table-responsive">
              <table className="ccm-bill-table">
            <thead>
              <tr>
                <th>Patient Name</th>
                <th>Clinical Min</th>
                <th>Provider Min</th>
                <th>Complexity</th>
                <th>Billing Path</th>
                <th>Eligible CPT</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {qualifyingPatients.length === 0 ? (
                <tr>
                  <td colSpan="7" className="ccm-bill-empty-cell">No qualifying patients found</td>
                </tr>
              ) : (
                qualifyingPatients.map((patient) => (
                  <tr
                    key={patient.enrollment_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const pid = resolvePatientRecordId(patient);
                      const prId = resolvePatientPracticeId(patient, practiceId);
                      const path = buildPatientDetailsPath(prId, pid);
                      if (path) navigate(path);
                    }}
                  >
                    <td>
                      <a
                        href="#"
                        style={{ color: '#007bff', textDecoration: 'none', fontWeight: 600 }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const pid = resolvePatientRecordId(patient);
                          const prId = resolvePatientPracticeId(patient, practiceId);
                          const path = buildPatientDetailsPath(prId, pid);
                          if (path) navigate(path);
                        }}
                      >
                        <strong>{patient.patient_name}</strong>
                      </a>
                    </td>
                    <td>{patient.clinical_staff_minutes ?? 0}</td>
                    <td>{patient.provider_minutes ?? 0}</td>
                    <td>{patient.is_complex ? 'Complex' : 'Non-Complex'}</td>
                    <td>{patient.billing_path || '—'}</td>
                    <td>
                      {patient.billing_blocked
                        ? 'BLOCKED'
                        : (Array.isArray(patient.eligible_cpt_codes)
                          ? patient.eligible_cpt_codes.join(', ')
                          : patient.eligible_cpt_code || 'N/A')}
                    </td>
                    <td>
                      <span className={`ccm-bill-badge ${
                        patient.billing_blocked ? 'ccm-bill-badge-no' :
                        patient.meets_minimum_time ? 'ccm-bill-badge-yes' : 'ccm-bill-badge-no'
                      }`}>
                        {patient.billing_blocked ? 'Blocked' : (patient.meets_minimum_time ? 'Ready' : 'Below threshold')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </Modal.Body>
        <div className="ccm-bill-modal-footer">
          <button className="ccm-bill-btn-secondary" onClick={() => setShowQualifyingModal(false)}>
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Billing;

